/**
 * 模块名称: 内部结算配置
 * @author zhuyan@372163.com
 */
import React, { useState, useEffect, useRef } from 'react'
import api from '@/api'
import HeaderSearch from '@/components/HeaderSearch'
import RangeNumberInput from '@/components/HeaderSearch/RangeNumberInput'
import {
  Table,
  Form,
  Select,
  Spin,
  Modal,
  Input,
  message,
  Button,
  Drawer,
  DatePicker,
} from 'antd'
import CorpFilter from '@/components/CorpFilter'
import Import from '@/components/Import'
import Export from '@/components/Export'
import EditSettle from './module/EditSettle'

const pageIndex = 1
const perPage = 10
let searchParam = {
  page: pageIndex,
  limit: perPage,
  supplierCompanyId: ''
}
const { Option } = Select
const operationList = [
  { id: '1', name: '为空' },
  { id: '2', name: '非空' },
]
const dateFormat = 'YYYY-MM-DD HH:mm'

const SettleConfig = () => {
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(pageIndex)
  const [limit, setPageSize] = useState(perPage)
  const [total, setTotal] = useState(0)
  const [dataSource, setDataSource] = useState([])
  const [historyVisible, setHistoryVisible] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyList, setHistoryList] = useState([])
  const [showImport, setShowImport] = useState(false)
  const [showExport, setShowExport] = useState(false)
  const [filteredParam, setFilteredParam] = useState({})
  const [companyList, setCompanyList] = useState([])
  const [projectList, setProjectList] = useState([])
  const [productList, setProductList] = useState([])
  const [editVisible, setEditVisible] = useState(false)
  const [editData, setEditData] = useState({})
  const ImportRef = useRef()
  const productsRef = useRef()
  const headerSearchRef = useRef()

  useEffect(() => {
    api.getCorp({ limit: global.paramsLimit }).then(res => {
      setCompanyList(res.list || [])
    })
    api.getsysProjectChilds({ pid: 0 }).then(data => {
      setProjectList(data)
    })
    resetParams()
    getList()
  }, [])

  const resetParams = () => {
    const { supplierCompanyId } = searchParam
    searchParam = {
      page: pageIndex,
      limit: perPage,
      ...(supplierCompanyId !== undefined && { supplierCompanyId })
    }
    setPageSize(perPage)
  }

  const getList = values => {
    if (values) {
      const { page, limit, supplierCompanyId } = searchParam
      searchParam = { page, limit, supplierCompanyId, ...values }
    }
    let params = { ...searchParam }
    setLoading(true)
    api.getInternalSettlementList(params).then(data => {
      setPage(searchParam.page)
      setPageSize(searchParam.limit)
      setTotal(data.count)
      setDataSource(data.list)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  const onChangeTable = (pagination, filters, sorter) => {
    searchParam.page = pagination.current
    searchParam.limit = pagination.pageSize
    getList()
  }

  // 记录弹窗
  const openHistoryModal = (record) => {
    setHistoryVisible(true)
    setHistoryLoading(true)
    const { companyId, supplierCompanyId, projectId, supplierId, productId } = record
    const params = {
      companyId,
      supplierCompanyId,
      projectId,
      supplierId,
      productId,
    }
    api.getInternalSettlementHistory(params).then(res => {
      const list = Array.isArray(res) ? res : (res && res.list) || []
      setHistoryList(list)
      setHistoryLoading(false)
    }).catch(() => setHistoryLoading(false))
  }

  const closeHistoryModal = () => {
    setHistoryVisible(false)
    setHistoryList([])
  }

  const onChangeProject = id => {
    if (id) {
      api.getsysProjectChilds({ pid: id }).then(data => {
        setProductList(data)
      })
    } else {
      setProductList([])
    }
    headerSearchRef.current.setFieldsValue({
      productId: undefined,
    })
  }

  const handleEdit = data => {
    setEditData(data)
    setEditVisible(true)
  }

  const cols = [
    {
      title: '序号',
      dataIndex: 'id',
      fixed: 'left',
      align: 'center',
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      fixed: 'left',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '项目ID',
      dataIndex: 'projectId',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '别名',
      dataIndex: 'projectAlias',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '产品名称',
      dataIndex: 'productName',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '产品ID',
      dataIndex: 'productId',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '转出公司',
      dataIndex: 'companyName',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '转入公司',
      dataIndex: 'supplierCompanyName',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '供应商',
      dataIndex: 'supplierName',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '内部主体结算单价',
      align: 'center',
      dataIndex: 'percent',
      render: (text) => text || '-'
    },
    {
      title: '更新时间',
      dataIndex: 'addTime',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '生效时间',
      dataIndex: 'effectiveTime',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '修改人',
      dataIndex: 'operateName',
      align: 'center',
      render: (text) => text || '-'
    },
    {
      title: '历史结算单价',
      dataIndex: 'historyPercent',
      align: 'center',
      render: (text) => {
        return text ? (
          <div style={{ whiteSpace: 'pre-line' }}>
            {text}
          </div>
        ) : (
          '-'
        );
      }
    },
    {
      title: '操作',
      dataIndex: 'actions',
      align: 'center',
      fixed: 'right',
      render: (text, record) => (
        <>
          <a onClick={() => handleEdit(record)}>修改</a>
          <a style={{ marginLeft: 10 }} onClick={() => openHistoryModal(record)}>记录</a>
        </>
      )
    }
  ]

  const searchItems = [
    {
      label: '项目名称',
      name: 'projectId',
      component: (
        <Select
          showSearch={true}
          optionFilterProp="children"
          placeholder="全部"
          allowClear
          dropdownMatchSelectWidth={false}
          onChange={onChangeProject}
        >
          {projectList.map((item, index) => <Option value={item.id} key={index}>{item.name}</Option>)}
        </Select>
      ),
    },
    {
      label: '产品名称',
      name: 'productId',
      component: (
        <Select
          showSearch={true}
          optionFilterProp="children"
          placeholder="全部"
          allowClear
          dropdownMatchSelectWidth={false}
        >
          {productList.map((item, index) => <Option value={item.id} key={index}>{item.name}</Option>)}
        </Select>
      ),
    },
    {
      label: '供应商',
      name: 'supplierName',
      component: <Input placeholder="请输入供应商名称" />,
    },
    {
      label: '结算单价范围',
      name: 'numTogether:percent',
      maxDefaultValue: 1,
      component: <RangeNumberInput placeholderMin="0.00" placeholderMax="1.00" />,
    },
    {
      label: '转出公司',
      name: 'companyId',
      component: (
        <Select
          showSearch={true}
          optionFilterProp="children"
          placeholder="全部"
          allowClear
          dropdownMatchSelectWidth={false}
        >
          <Option value="select-all">全部</Option>
          {companyList.map((item, index) => <Option value={item.id} key={index}>{item.name}</Option>)}
        </Select>
      ),
    },
    {
      label: '结算单价',
      name: 'percentStatus',
      component: (
        <Select placeholder="全部" allowClear>
          <Option value="select-all">全部</Option>
          {operationList.map((item, index) => <Option value={item.id} key={index}>{item.name}</Option>)}
        </Select>
      ),
    },
    {
      label: '结算时间',
      name: 'timeSettleConfig:effectiveTime',
      component: (
        <DatePicker 
          placeholder="请选择" 
          showTime
          format={dateFormat}
          style={{ width: '100%' }}
        />
      ),
    },
  ]

  const onChangeCorp = (data) => {
    searchParam = {
      ...searchParam,
      supplierCompanyId: Array.isArray(data) ? data.map(item => item.id).join(',') : (data && data.id) || '',
      page: 1,
    }
    getList()
  }

  // 导入确定
  const onConfirmImport = (fileList) => {
    if (fileList[0].status !== 'removed') {
      api.getImportPercentImportExcel({
        path: fileList[0].url
      }).then(res => {
        getList()
        message.success('导入成功')
        ImportRef.current.onLoading()
        setShowImport(false)
      }).catch(res => {
        ImportRef.current.onLoading()
      })
    } else {
      message.error('请导入模板')
    }
  }

  const onShowImportDialog = () => {
    ImportRef.current.onRemoved()
    setShowImport(true)
  }

  const onShowExportDialog = () => {
    setShowExport(true)
    const { page, limit, ...filteredSearchParam } = searchParam
    setFilteredParam(filteredSearchParam)
  }


  return (
    <div className="config-settleConfig-container">
      <CorpFilter onChange={onChangeCorp} label="转入公司" />
      <Spin spinning={loading} tip="Loading...">
        <div style={{ height: 10 }}></div>
        <HeaderSearch
          search={searchItems}
          getList={getList}
          resetParams={resetParams}
          hasShowAll={false}
          ref={headerSearchRef}
        ></HeaderSearch>
        <div className='line'></div>
        <div className="config-settleConfig-btn">
          <Button type="primary" className='stock-btn-space' onClick={onShowImportDialog}>数据导入</Button>
          <Button type="primary" className='stock-btn-space' onClick={onShowExportDialog}>数据导出</Button>
        </div>
        <Table
          size="small"
          columns={cols}
          rowKey="id"
          dataSource={dataSource}
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultCurrent: pageIndex,
            pageSize: limit,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            current: page,
            pageSizeOptions: ['10', '50', '100', '500', '1000', '2000']
          }}
          onChange={onChangeTable}
        ></Table>
        <Modal
          title="结算单价修改记录"
          visible={historyVisible}
          onCancel={closeHistoryModal}
          footer={[
            <Button key="close" type='primary' onClick={closeHistoryModal}>关闭</Button>
          ]}
          destroyOnClose={true}
          width={1000}
        >
          <Table
            size="small"
            rowKey="id"
            dataSource={historyList}
            loading={historyLoading}
            pagination={false}
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                align: 'center',
                render: (text, record, index) => historyList.length - index
              },
              {
                title: '内部主体结算单价',
                dataIndex: 'percent',
                align: 'center',
                render: text => text || '-'
              },
              {
                title: '生效时间',
                dataIndex: 'effectiveTime',
                align: 'center',
                render: text => text || '-'
              },
              {
                title: '修改方式',
                dataIndex: 'type',
                align: 'center',
                render: text => ({ 1: '页面修改', 2: '数据导入' }[text] || '-')
              },
              {
                title: '附件',
                dataIndex: 'attachment',
                align: 'center',
                width: 200,
                render: text => {
                  return text ? (
                    <>
                      {JSON.parse(text).map(item => (
                        <div>
                          <a href={item.url}>{item.filename}</a>
                        </div>
                      ))}
                    </>
                  ) : '-'
                }
              },
              {
                title: '备注信息',
                dataIndex: 'desc',
                align: 'center',
                render: text => text || '-'
              },
              {
                title: '更新时间',
                dataIndex: 'addTime',
                align: 'center',
                render: text => text || '-'
              },
              {
                title: '修改人',
                dataIndex: 'operateName',
                align: 'center',
                render: (text) => text || '-'
              }
            ]}
          />
        </Modal>
      </Spin>
      <Export
        show={showExport}
        onCancel={() => {
          setShowExport(false)
          productsRef.current.changeVal()
        }}
        tplUrl="getInternalSettlementExportTpl"
        fieldsUrl="getInternalSettlementTplItems"
        saveUrl="getInternalSettlementSaveExportTpls"
        exportUrl="getInternalSettlementExport"
        method="export"
        parame={{ ExportType: 'internalsettlement.export', ...filteredParam }}
        cRef={productsRef}
      />
      <Import
        onSearch={onConfirmImport}
        visibleFile={showImport}
        onCloseImport={() => setShowImport(false)}
        ImportTemplate="/api/File/DownTemplate?url=/templates/内部结算价格.xlsx&name=内部结算价格"
        ImportRef={ImportRef}
        title="数据导入"
      />
      <Drawer
        title="内部结算单价修改"
        width={600}
        onClose={() => setEditVisible(false)}
        visible={editVisible}
        destroyOnClose={true}
      >
        <EditSettle 
          setEditVisible={setEditVisible}
          getList={getList}
          editData={editData}
        />
      </Drawer>
    </div>
  )
}

export default Form.create()(SettleConfig)
